import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/store/Logo";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 paw-bg">
      <div className="w-full max-w-sm">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-moss hover:text-coral mb-5">
          <ArrowLeft className="w-4 h-4" /> Volver a la tienda
        </Link>
        <div className="bg-white rounded-3xl border border-[#E3E7E2] card-shadow p-7">
          <div className="flex flex-col items-center text-center mb-6">
            <Logo size={56} className="mb-3" />
            <h1 className="font-display text-2xl font-bold text-charcoal">Panel de administrador</h1>
            <p className="text-sm text-muted-moss mt-1">Ingresa para gestionar tu tienda</p>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-moss" />
              <input
                data-testid="admin-login-email"
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Usuario"
                required
                className="w-full pl-10 pr-4 py-3 rounded-2xl bg-cream border border-[#E3E7E2] text-sm outline-none focus:border-coral"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-moss" />
              <input
                data-testid="admin-login-password"
                type={show ? "text" : "password"}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Contraseña"
                required
                className="w-full pl-10 pr-12 py-3 rounded-2xl bg-cream border border-[#E3E7E2] text-sm outline-none focus:border-coral"
              />
              <button type="button" data-testid="admin-login-toggle-password" onClick={() => setShow((v) => !v)} aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-moss hover:text-coral">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {error && <p data-testid="login-error" className="text-sm text-destructive font-medium">{error}</p>}

            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-coral text-white font-bold hover:bg-coral-dark active:scale-95 transition-all disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Ingresar"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
